const PROJECTS_URL = '/api/projects';
const GOOGLE_URL = '/api/google-accounts';
const META_URL = '/api/meta-accounts';

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, init);
  if (!response.ok) {
    const body = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error(body.error?.message ?? body.error ?? `Request failed: ${response.status}`);
  }
  return response.json() as Promise<T>;
}

// ─── Types ──────────────────────────────────────────────────────────

export type IntegrationType = 'ga4' | 'gsc' | 'ads' | 'ig';

export interface CompanyProjectIntegration {
  id: number;
  project_id: number;
  integration_type: IntegrationType;
  google_account_id: number | null;
  meta_account_id: number | null;
  config_json: string;
  created_at: string;
  updated_at: string;
}

export interface IntegrationWithAccountDTO {
  id: number;
  integration_type: IntegrationType;
  google_account_id: number | null;
  google_account_alias: string | null;
  meta_account_id: number | null;
  meta_account_alias: string | null;
  config_json: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

export interface GA4PropertyDTO {
  propertyId: string;
  displayName: string;
}

export interface GSCSiteDTO {
  siteUrl: string;
}

export interface AdsCustomerDTO {
  customerId: string;
  descriptiveName: string | null;
}

export interface IGBusinessAccountDTO {
  igUserId: string;
  username: string;
}

export interface TestConnectionResult {
  ok: boolean;
  message?: string;
  needsReauth?: boolean;
}

export interface IntegrationLabel {
  type: IntegrationType;
  label: string;
  icon: string;
  accountKind: 'google' | 'meta';
}

export const INTEGRATION_LABELS: IntegrationLabel[] = [
  { type: 'ga4', label: 'Google Analytics', icon: '📊', accountKind: 'google' },
  { type: 'gsc', label: 'Google Search Console', icon: '🔍', accountKind: 'google' },
  { type: 'ads', label: 'Google Ads', icon: '💰', accountKind: 'google' },
  { type: 'ig', label: 'Instagram', icon: '📷', accountKind: 'meta' },
];

// ─── Integration CRUD ────────────────────────────────────────────────

export async function upsertIntegration(
  projectId: number,
  integrationType: IntegrationType,
  data: { google_account_id?: number; meta_account_id?: number; config_json?: Record<string, unknown> },
): Promise<CompanyProjectIntegration> {
  return fetchJson<CompanyProjectIntegration>(`${PROJECTS_URL}/${projectId}/integrations/${integrationType}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
}

export async function listIntegrations(projectId: number): Promise<IntegrationWithAccountDTO[]> {
  return fetchJson<IntegrationWithAccountDTO[]>(`${PROJECTS_URL}/${projectId}/integrations`);
}

export async function deleteIntegration(projectId: number, integrationType: IntegrationType): Promise<void> {
  const response = await fetch(`${PROJECTS_URL}/${projectId}/integrations/${integrationType}`, { method: 'DELETE' });
  if (response.status !== 204) {
    throw new Error(`Delete failed: ${response.status}`);
  }
}

export async function testConnection(projectId: number, integrationType: IntegrationType): Promise<TestConnectionResult> {
  return fetchJson<TestConnectionResult>(`${PROJECTS_URL}/${projectId}/integrations/${integrationType}/test`, {
    method: 'POST',
  });
}

// ─── Listing helpers ─────────────────────────────────────────────────

export async function listGA4Properties(googleAccountId: number): Promise<GA4PropertyDTO[]> {
  return fetchJson<GA4PropertyDTO[]>(`${GOOGLE_URL}/${googleAccountId}/ga4-properties`);
}

export async function listGSCSites(googleAccountId: number): Promise<GSCSiteDTO[]> {
  return fetchJson<GSCSiteDTO[]>(`${GOOGLE_URL}/${googleAccountId}/gsc-sites`);
}

export async function listAdsCustomers(googleAccountId: number): Promise<AdsCustomerDTO[]> {
  return fetchJson<AdsCustomerDTO[]>(`${GOOGLE_URL}/${googleAccountId}/ads-customers`);
}

export async function listIGBusinessAccounts(metaAccountId: number): Promise<IGBusinessAccountDTO[]> {
  return fetchJson<IGBusinessAccountDTO[]>(`${META_URL}/${metaAccountId}/ig-accounts`);
}
